import rimg from '@pixelunion/rimg-shopify';
import 'intersection-observer';
import EventHandler from '@pixelunion/events';
import { transition } from '@pixelunion/animations';

import ProductList from '../components/ProductList';
import Rte from '../utils/Rte';

function setQueryParams() {
  window.Shopify.queryParams = {};

  if (window.location.search.length) {
    let aKeyValue;
    const aCouples = window.location.search.substr(1).split('&');

    for (let i = 0; i < aCouples.length; i++) {
      aKeyValue = aCouples[i].split('=');

      // Reset the page number when we apply (i.e. don't add it to params)
      if ((aKeyValue.length > 1) && (aKeyValue[0] !== 'page')) {
        window
          .Shopify
          .queryParams[decodeURIComponent(aKeyValue[0])] = decodeURIComponent(aKeyValue[1]);
      }
    }
  }
}

function updateSortFilter(e) {
  let search;
  const Sorting = {};
  Sorting.sort_by = document.querySelector('[data-sorting] select').value;

  const currentVendorSelected = e.target.closest('[data-sorting]').classList.contains('vendor-collection');
  const facetedFiltersEnabled = document.querySelector('[data-faceted-filter]');

  if (currentVendorSelected || facetedFiltersEnabled) {
    const currentSearch = window.location.search;

    const searchParts = currentSearch.split('&');

    for (let index = 0; index < searchParts.length; index++) {
      const part = searchParts[index];
      if (part.indexOf('sort_by') !== -1) {
        searchParts.splice(index, 1);
      }
    }

    search = searchParts.join('&');
    window.location.search = `${search}&${Object.keys(Sorting).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(Sorting[k])}`).join('&')}`;
  } else {
    window.location.search = Object.keys(Sorting).map(k => `${encodeURIComponent(k)}=${encodeURIComponent(Sorting[k])}`).join('&');
  }
}

export default class Collection {
  constructor(section) {
    this.events = new EventHandler();
    this.el = section.el;
    this.data = section.data;
    this._infiniteScrollHandler = this._infiniteScrollHandler.bind(this);
    this.productList = new ProductList({ el: this.el, id: `shopify-section-${section.id}` });
    this.rtes = [];
    this.collectionTagFilterSelect = document.querySelector('[data-tag-filtering] select');
    this.collectionSortFilterSelect = document.querySelector('[data-sorting] select');

    this.el.querySelectorAll('[data-rich-text-area]').forEach(el => this.rtes.push(new Rte({ el })));

    this.filterInputs = null;
    this.dropdowns = this.el.querySelectorAll('[data-filter-group]');
    this.summaryEls = this.el.querySelectorAll('[data-filter-group-summary]');
    this.filterContent = this.el.querySelectorAll('[data-filter-group-content]');

    this.filterCheckboxes = this.el.querySelectorAll('.filter-icon--checkbox');
    this.fillAnimations = {};
    this.checkAnimations = {};

    this.closedState = 'closed';
    this.openState = 'open';
    this.disclosureAnimations = {};

    this.filterRange = this.el.querySelectorAll('[data-filter-range]');
    this.rangeNames = [];

    this.filterRange.forEach(el => {
      this.rangeNames.push(el.name);
    });

    this.updateTimeout = null;

    this._initFilterEvents = this._initFilterEvents.bind(this);
    this._updatePrice = this._updatePrice.bind(this);
    this._buildFilterQuery = this._buildFilterQuery.bind(this);



    if (section.type === 'static-collection') {
      if (this.dropdowns.length) {
        this._setDropdownHeight();
      }

      this._initAnimations();
      this._initFilterEvents();
      setQueryParams();
      this._initializeInfiniteScroll();
      this._bindEvents();
    }
  }

  _bindEvents() {
    if (this.collectionSortFilterSelect) {
      this.events.register(this.el.querySelector('[data-sorting] select'), 'change', updateSortFilter);
    }

    if (this.enableInfiniteScroll) {
      this.events.register(this.showMoreButton, 'click', this._infiniteScrollHandler);
    }
  }

  _initializeInfiniteScroll() {
    // Infinite scroll
    if (this.data.pagination_method.indexOf('infinite_scroll') > -1
      && this.data.products_per_page < this.data.total_products
      && window.location.search.indexOf('page') === -1
    ) {
      this.loadMoreTarget = this.el.querySelector('[data-infinite-scroll-target]');
      this.showMoreButton = this.el.querySelector('[data-infinite-scroll-show-more]');
      this.showMoreButton.style.display = 'inline-block';
      this.productsLoadedCount = this.el.querySelectorAll('[data-product-item-id]').length;

      // If we're infinite scrolling, we want to hide pagination
      // while still keeping it accessible for screenreaders.
      const pagination = this.el.querySelector('.pagination');
      pagination.style.position = 'absolute';
      pagination.style.left = '-9999px';

      this.currentPage = 1;
      this.fullCollectionLoaded = false;
      this.enableInfiniteScroll = true;

      if (this.data.pagination_method === 'infinite_scroll') {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              this.scrollTargetVisible = true;
              this._infiniteScroll();
            } else {
              this.scrollTargetVisible = false;
            }
          });
        });

        observer.observe(this.loadMoreTarget);
      }
    }
  }

  _infiniteScrollHandler() {
    // Use handler so _infiniteScroll isn't called with event as argument
    this._infiniteScroll();
  }

  _infiniteScroll(autoScrollCount = 0) {
    if (this.fullCollectionLoaded || autoScrollCount > 2) {
      return;
    }
    const currentPage = `page=${this.currentPage}`;
    this.currentPage++;

    const nextPage = `page=${this.currentPage}`;
    let url = `${window.location.pathname}${window.location.search}`;

    if (window.location.search.indexOf('page') > -1) {
      url = url.replace(currentPage, nextPage);
    } else if (window.location.search.indexOf('?') > -1) {
      url = `${url}&${nextPage}`;
    } else {
      url = `${url}?${nextPage}`;
    }

    this.showMoreButton.classList.add('loading');
    this.showMoreButton.innerHTML = this.showMoreButton.dataset.infiniteScrollLoading;

    const xhr = new XMLHttpRequest();

    xhr.onload = () => {
      const { response } = xhr;
      const products = response.querySelectorAll('[data-product-item-id]');
      const collectionGrid = document.querySelector('[data-collection-grid]');

      products.forEach(product => {
        collectionGrid.appendChild(product);
        this.productsLoadedCount++;
        this.productList.reload();

        // Add a timeout to give rimg a little more time to load placeholder SVG
        // to prevent images from loading at the wrong size in Firefox
        setTimeout(() => rimg.watch(product), 2);
      });

      if (this.productsLoadedCount === this.data.total_products) {
        // we've loaded the full collection
        this.fullCollectionLoaded = true;
        this.showMoreButton.style.display = 'none';
      }

      this.showMoreButton.classList.remove('loading');
      this.showMoreButton.innerHTML = this.showMoreButton.dataset.infiniteScrollShowMore;

      // If target is still in viewport, we want to load more items.
      // But let's keep track of how many times we've done this, and
      // take a bit of time to let IntersectionObserver catch up.
      setTimeout(() => {
        if (this.scrollTargetVisible) {
          this._infiniteScroll(autoScrollCount + 1);
        }
      }, 200);
    };
    xhr.open('GET', url, true);
    xhr.responseType = 'document';
    xhr.send();
  }

  _setDropdownHeight() {
    const filterWrapper = this.el.querySelector('.collection-page__filter-wrapper');
    let maxHeight = 0;

    this.dropdowns.forEach(el => {
      const height = el.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });

    filterWrapper.style.setProperty('--closed-filter-height', `${maxHeight}px`);
  }

  _initFilterEvents() {
    /* For radio buttons */
    this.filterInputs = this.el.querySelectorAll('[data-filter-input]');

    this.filterInputs.forEach(filter => {
      this.events.register(filter, 'click', e => {
        e.preventDefault();
        const target = e.currentTarget;
        const url = target.dataset.url;

        if (target.dataset.hasOwnProperty('disabled')) return;

        const handle = target.getAttribute('data-handle');

        let animateTo = 'checked';

        if (target.hasAttribute('data-filter-active')) {
          animateTo = 'unchecked';
          target.removeAttribute('data-filter-active');
        } else {
          target.setAttribute('data-filter-active', '');
        }

        if (this.fillAnimations[handle] && this.checkAnimations[handle]) {
          this.fillAnimations[handle].animateTo(animateTo);
          this.checkAnimations[handle].animateTo(animateTo).then(() => {
            window.location = url;
          });
        }
      });
    });

    this.filterContent.forEach(content => {
      this.events.register(window, 'resize', () => this._setStyleVariables(content));
    });

    this.events.register(window, 'keyup', e => {
      if (e.key === 'Escape') {
        this._closeAllDropdowns();
      }
    });

    /* For range input */
    this.filterRange.forEach(filter => {
      this.events.register(filter, 'keyup', e => this._updatePrice(e.currentTarget));
      this.events.register(filter, 'change', e => this._updatePrice(e.currentTarget));
    });

    this.summaryEls.forEach(summary => {
      this.events.register(summary, 'click', event => this._animateDisclosure(event));
    });

    /* When clicking outside dropdown element, close dropdown */
    this.events.register(document.body, 'click', e => {
      this.dropdowns.forEach(dropdown => {
        if (dropdown.classList.contains('dropdown-expanded')) {
          if (!$(e.target).closest('.collection-filters__filter-group').length) {
            this._animateDisclosure(e);
          }
        }
      });
    });
  }

  /*
   * Initialize animations on checkbox container
   * using filter tag as JSON key
   */
  _initAnimations() {
    this.filterCheckboxes.forEach(el => {
      const tagHandle = el.dataset.handle;
      const checkmark = el.querySelector('.checkmark');
      const checkmarkCheck = el.querySelector('.checkmark__check');
      let state = 'unchecked';

      if (el.closest('[data-filter-input]').hasAttribute('data-filter-active')) {
        state = 'checked';
      }

      const fillAnimation = transition({ el: checkmark, state });
      const checkAnimation = transition({ el: checkmarkCheck, state });

      this.fillAnimations[tagHandle] = fillAnimation;
      this.checkAnimations[tagHandle] = checkAnimation;
    });

    this.summaryEls.forEach(summary => {
      const filterList = summary.nextElementSibling;
      const currentDetailsEl = summary.closest('[data-filter-group]');
      const index = summary.dataset.filterGroupIndex;
      this.disclosureAnimations[index] = transition({ el: filterList, state: this.closedState });
      currentDetailsEl.style.setProperty('--closed-height', `${summary.offsetHeight}px`);
    });
  }

  _buildFilterQuery(element) {
    let searchParameters;

    // If input is price range, the url_to_add property is not available, so we
    // need to build the query manually
    if (element.classList.contains('collection-filters__filter-range-input')) {
      const currentSearch = window.location.search.replace('?', '');
      const formData = new FormData(element.closest('form'));
      const newSearch = new URLSearchParams(formData).toString();

      // Filter through current search string to see if price range input already exists,
      // if it does, create new string without current price range parameters
      const splitCurrentSearch = currentSearch.split('&');

      let filteredCurrentSearch = splitCurrentSearch.filter(search => {
        if (search.includes(this.rangeNames[0]) || search.includes(this.rangeNames[1])) {
          return false;
        }

        return true;
      });

      filteredCurrentSearch = filteredCurrentSearch.join('&');

      // If current search parameters exist, append new search parameters
      // on to existing parameters
      if (filteredCurrentSearch.length > 0) {
        searchParameters = `${filteredCurrentSearch}&${newSearch}`;
      } else {
        searchParameters = `${newSearch}`;
      }
    }

    let urlQueryString = '';

    urlQueryString = `?${searchParameters}`;

    return urlQueryString;
  }

  _updatePrice(element) {
    // cancel any pending requests
    if (this.updateTimeout !== null) {
      clearTimeout(this.updateTimeout);
    }

    // Wait for user to finish entering input before reloading page
    this.updateTimeout = setTimeout(() => {
      this._getFilteredResults(element);
    }, 2000);
  }

  _getFilteredResults(element) {
    const filterQuery = this._buildFilterQuery(element);
    window.location.search = filterQuery;
  }

  _animateDisclosure(event) {
    event.preventDefault();
    const summary = event.currentTarget;
    const filterList = summary.nextElementSibling;
    const currentDetailsEl = summary.closest('[data-filter-group]');

    /* Handle dropdowns on click event outside disclosure */
    if (filterList == null) {
      this._closeAllDropdowns();
    } else {
      if (filterList.dataset.animation) return;

      if (filterList.dataset.animationState === this.openState) {
        this._closeDisclosure(currentDetailsEl);
      } else {
        /* Close any open dropdowns before opening new dropdown */
        this._closeAllDropdowns();
        this._openDisclosure(currentDetailsEl);
      }
    }
  }

  _openDisclosure(detailsEl) {
    const index = detailsEl.querySelector('[data-filter-group-summary]').dataset.filterGroupIndex;
    detailsEl.setAttribute('open', '');

    this.disclosureAnimations[index]
      .animateTo(this.openState, {
        onStart: ({ el }) => {
          this._setStyleVariables(el);
        }
      });

    detailsEl.classList.add('dropdown-expanded');
  }

  _setStyleVariables(el) {
    const main = document.querySelector('#main-content');
    const elementBounds = el.getBoundingClientRect();
    const rightBounds = elementBounds.right;
    const leftBounds = elementBounds.left;

    const mobileWidth = main.getBoundingClientRect().width;

    if (rightBounds >= window.innerWidth) {
      el.classList.add('filters--overflow-right');
    }

    if (leftBounds <= 0) {
      el.classList.add('filters--overflow-left');
    }

    el.style.setProperty('--mobile-width', `${mobileWidth}px`);
    el.style.setProperty('--open-height', `${el.offsetHeight}px`);
  }

  _closeDisclosure(detailsEl) {
    const index = detailsEl.querySelector('[data-filter-group-summary]').dataset.filterGroupIndex;

    this.disclosureAnimations[index]
      .animateTo(this.closedState)
      .then(() => {
        detailsEl.removeAttribute('open');
      });

    detailsEl.classList.remove('dropdown-expanded');
  }

  _closeAllDropdowns() {
    this.dropdowns.forEach(dropdown => {
      if (dropdown.classList.contains('dropdown-expanded')) {
        this._closeDisclosure(dropdown);
      }
    });
  }

  onSectionUnload() {
    this.events.unregisterAll();
    this.rtes.forEach(rte => rte.unload());

    if (this.productList) {
      this.productList.unload();
    }
  }
}
